/**
 * The platform console and the partner view: the same shell, their own sidebar.
 * @file src/web/shared/ConsoleLayoutRoute.tsx
 *
 * Each admits exactly one kind of account (see home.ts). Anybody else is sent
 * to their own home — a firm admin who types /platform lands back in their
 * firm, not on an "Access denied" page that confirms the console exists.
 * The API refuses them regardless; this is so the screen never pretends.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from './api';
import { AuthGuard } from '@/features/auth/components';
import { useAuth } from '@/features/auth/hooks';
import { Shell } from './DashboardLayoutRoute';
import { PARTNER_NAV, PLATFORM_NAV } from './console-nav';
import { accountKind, homeFor } from './home';

/** The partner's own name, for the partner view's header. */
function usePartnerName(enabled: boolean): string | null {
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    api
      .get<{ partner: { name: string } }>('/api/partner/summary')
      .then((r) => live && setName(r.partner.name))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [enabled]);
  return name;
}

function ConsoleFor({ kind }: { kind: 'platform' | 'partner' }) {
  const { user } = useAuth();
  const partnerName = usePartnerName(kind === 'partner' && accountKind(user) === 'partner' && !user?.mustChangePassword);
  if (user?.mustChangePassword) return <Navigate to="/auth/change-password" replace />;
  if (accountKind(user) !== kind) return <Navigate to={homeFor(user)} replace />;
  return (
    <Shell
      items={kind === 'platform' ? PLATFORM_NAV : PARTNER_NAV}
      firmClock={false}
      /* /dashboard is the firm's page; platform and partner staff have no
       * pipeline of their own to ask about. */
      ask={false}
      profileHref="/auth/change-password"
      label={kind === 'platform' ? 'Platform admin' : ['Partner admin', partnerName]}
    />
  );
}

export function PlatformLayoutRoute() {
  return (
    <AuthGuard>
      <ConsoleFor kind="platform" />
    </AuthGuard>
  );
}

export function PartnerLayoutRoute() {
  return (
    <AuthGuard>
      <ConsoleFor kind="partner" />
    </AuthGuard>
  );
}
